import React from "react";
import { useRegisterAuditReportDialog, RegisterAuditReportDialogProps } from "./RegisterAuditReportDialog.hooks";
import { arrow, leftWhiteArrowButton } from "../../../common/icons";
import { DotLegalDialog, DotLegalHeader, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { apiUrls } from "../../../common/api/apiUrls";
import DotLegalSelectApi from "../../../common/components/dotLegalSelectApi/DotLegalSelectApi";
import DotLegalMultiSelectApi from "../../../common/components/dotLegalMultiSelectApi/DotLegalMultiSelectApi";
import { Box } from "@mui/material";
import { QuestionnaireSelectableItem } from "../LegalEntityAudit.types";
import GroupEntityStep from "../groupEntityStep/GroupEntityStep";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import AuditTemplateLinkWrapper from "../../../common/AuditTemplateLinkWrapper";
import DotLegalDocumentUploadWrapper from "@common/components/DotLegalDocumentUploadWrapper";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";

function RegisterAuditReportDialog(props: RegisterAuditReportDialogProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();

    const {
        model,
        step,
        onQuestionnaireChanged,
        onResponsibleChanged,
        onSystemsChanged,
        onProcessingActivitiesChanged,
        onGroupEntityChanged,
        onNextOrSaveClick,
        onPreviousButtonClick,
        isSaving,
        validation,
        selectableGroupEntitiesData,
        selectableGroupEntitiesLoading,
        selectableResponsiblesData,
        selectableResponsiblesLoading,
        documentUploadModel,
        setDocumentUploadModel,
        showSelectGroupEntityStep,
        showDocumentError,
        isUploading,
        uploadPercentage,
    } = useRegisterAuditReportDialog(props);

    const formStep = (
        <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
            <Box sx={{ width: 450 }}>
                <DotLegalHeader headerStyle={"small"} fontWeight={400} center>
                    {translateString("legalEntityAuditRegisterAuditInfo")}
                </DotLegalHeader>

                <DotLegalSelectApi<QuestionnaireSelectableItem>
                    url={apiUrls().questionnaires.selectableItems}
                    onChange={onQuestionnaireChanged}
                    label={translateString("template")}
                    noOptionsLabel={translateString("noOptions")}
                    selectedItem={model.questionnaireId}
                    errorText={validation.getErrorsForDisplay("questionnaireId")}
                    disableClearable
                />

                <DotLegalSelect
                    options={selectableResponsiblesData}
                    onChange={onResponsibleChanged}
                    label={translateString("responsible")}
                    selectedItem={model.responsibleId}
                    noOptionsLabel={translateString("noOptions")}
                    isLoading={selectableResponsiblesLoading}
                    errorText={validation.getErrorsForDisplay("responsibleId")}
                />

                {permissions.systemPermissions.read && (
                    <DotLegalMultiSelectApi
                        url={apiUrls().systems.selectableItemsByLegalEntityId(props.legalEntityId)}
                        onChange={onSystemsChanged}
                        label={translateString("systems")}
                        noOptionsLabel={translateString("noOptions")}
                        chosenOptions={model.systemIds}
                        disabled={model.processingActivityIds.length > 0}
                    />
                )}

                {permissions.processingActivityPermissions.read && (
                    <DotLegalMultiSelectApi
                        url={apiUrls().processingActivites.selectableItemsByAccess}
                        onChange={onProcessingActivitiesChanged}
                        label={translateString("processingActivities")}
                        noOptionsLabel={translateString("noOptions")}
                        chosenOptions={model.processingActivityIds}
                        disabled={model.systemIds.length > 0}
                    />
                )}

                <Box marginTop={2}>
                    <DotLegalDocumentUploadWrapper
                        showError={showDocumentError}
                        viewModel={documentUploadModel}
                        onDataChange={(uploadModel) => {
                            setDocumentUploadModel(uploadModel);
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );

    const groupEntityStep = (
        <GroupEntityStep
            showGdpoIfAvailable
            onGroupEntityChanged={onGroupEntityChanged}
            errormsg={validation.getErrorsForDisplay("groupEntityId")}
            groupEntityId={model.groupEntityId}
            selectableGroupEntitiesData={selectableGroupEntitiesData}
            infoTranslationKey={"legalEntityAudtiRegisterAuditGroupEntity"}
            checkboxTranslationKey={"legalEntityAuditRegisterAuditEntireGroup"}
            checkboxTooltip={""}
        />
    );

    return (
        <DotLegalDialog
            header={translateString("registerAuditReport")}
            size={"md"}
            buttonOkIcon={step === "form" && showSelectGroupEntityStep ? arrow : undefined}
            buttonOkText={step === "form" && showSelectGroupEntityStep ? translateString("next") : translateString("save")}
            onDialogClose={props.onDialogClose}
            onOkClick={onNextOrSaveClick}
            previousButtonText={translateString("previous")}
            previousButtonClick={step !== "form" ? onPreviousButtonClick : undefined}
            previousButtonIcon={leftWhiteArrowButton}
            open
            buttonRight
            inProgress={isSaving}
            okButtonDisabled={selectableGroupEntitiesLoading}
        >
            {step === "form" ? formStep : groupEntityStep}
            <UploadProgressBar isUploading={isUploading} uploadPercentage={uploadPercentage} />
        </DotLegalDialog>
    );
}

export default RegisterAuditReportDialog;
